if (!customElements.get('clerk-cart-cross-sell')) {
  customElements.define(
    'clerk-cart-cross-sell',
    class ClerkCartCrossSell extends HTMLElement {
      constructor() {
        super();

        /** @type {import('./types').ItemCarousel | null} */
        this.carousel = this.querySelector('item-carousel');
        this.list = this.querySelector('[data-products-list]');
        this.endpoint = this.dataset.endpoint || 'recommendations/popular';
        this.limit = this.dataset.limit || 6;
        this.productIds = Array.from(new Set(this.dataset.productIds?.split(',').filter(Boolean) || []));
        this.controller = null;
        this.loadOperationId = 0;
        this.handleCartUpdatedBound = this.handleCartUpdated.bind(this);
      }

      async connectedCallback() {
        window.addEventListener('cart-updated', this.handleCartUpdatedBound);

        if (this.hasCartContext()) {
          await this.loadRecommendations();
        }
      }

      hasCartContext() {
        if (this.dataset.position === 'mini_cart_tray') {
          return this.productIds.length > 0;
        }

        return true;
      }

      disconnectedCallback() {
        window.removeEventListener('cart-updated', this.handleCartUpdatedBound);
        this.controller?.abort();
      }

      async loadRecommendations() {
        const operationId = ++this.loadOperationId;

        this.setAttribute('data-loading', 'true');
        this.removeAttribute('data-products-loaded');
        this.classList.add('visually-hidden');
        this.hidden = true;

        try {
          const products = await this.getRecommendedProducts();
          const cards = await this.getProductCards(products);

          if (!this.carousel || !this.list) return;
          if (this.loadOperationId !== operationId) return;

          if (cards.length > 0) {
            this.list.replaceChildren(...cards);
            this.setAttribute('data-products-loaded', 'true');
            this.hidden = false;
            this.classList.remove('visually-hidden');
            this.carousel.instance?.update();
            this.enableClerkClickTracking();
          } else if (this.list.children.length === 0) {
            this.hidden = true;
          }
        } catch {
          if (this.list?.children.length === 0) {
            this.hidden = true;
          }
        } finally {
          if (this.loadOperationId === operationId) {
            this.removeAttribute('data-loading');
          }
        }
      }

      handleCartUpdated(/** @type {Event} */ event) {
        if (!(event instanceof CustomEvent)) return;

        const items = Array.isArray(event.detail?.items) ? event.detail.items : [];
        const productIds = items
          .map((/** @type {{ product_id?: number | string }} */ item) => String(item.product_id))
          .filter(Boolean);
        const nextProductIds = Array.from(new Set(productIds));
        const signature = nextProductIds.join(',');

        if (signature === this.productIds.join(',')) return;

        this.productIds = nextProductIds;
        this.dataset.productIds = signature;

        if (this.dataset.position === 'mini_cart_tray' && nextProductIds.length === 0) {
          this.hidden = true;
          this.classList.add('visually-hidden');
          this.removeAttribute('data-products-loaded');
          this.list?.replaceChildren();

          return;
        }

        this.loadRecommendations();
      }

      enableClerkClickTracking() {
        if (typeof window.Clerk === 'function') {
          window.Clerk('click', '*[data-clerk-product-id]');
        }
      }

      getRecommendedUrl() {
        const url = `https://api.clerk.io/v2/${this.endpoint}?key=nqfwzYFvqB1afrgxh6Dm62gL7yFOnyod&limit=${this.limit}&visitor=auto&labels=["cart-cross-sell"]&attributes=url,id,tags&filter=tags+contains%20not+"combined-listing"`;

        const endpointsWithProductContext = ['recommendations/most_sold_with', 'recommendations/visitor/complementary'];

        if (!endpointsWithProductContext.includes(this.endpoint)) return url;

        if (this.productIds.length === 0) return null;

        return `${url}&products=[${this.productIds.join(',')}]`;
      }

      /**
       * @returns {Promise<import('./types').ClerkProductData[]>} - The recommended Clerk products
       */
      async getRecommendedProducts() {
        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        const recommendedUrl = this.getRecommendedUrl();

        if (!recommendedUrl) return [];

        const response = await fetch(recommendedUrl, {
          method: 'GET',
          headers: { accept: 'application/json' },
          signal,
        }).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (response && response.ok) {
          const data = await response.json();

          return (data.product_data || [])
            .filter((/** @type {import('./types').ClerkProductData} */ product) => {
              return !this.productIds.includes(String(product.id));
            })
            .map((/** @type {import('./types').ClerkProductData} */ product) => ({
              url: product.url,
              id: String(product.id),
            }));
        }

        throw new Error(response?.statusText || 'Failed to fetch recommendations');
      }

      /**
       * @param {import('./types').ClerkProductData[]} products - The Clerk products to fetch cards for
       * @returns {Promise<HTMLElement[]>} - The product cards
       */
      async getProductCards(products) {
        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        if (!products || !Array.isArray(products) || products.length === 0) {
          return [];
        }

        try {
          const requests = products.map(async (product) => {
            const productUrl = new URL(product.url, location.origin);
            productUrl.searchParams.set('section_id', 'section-main-product');

            const response = await fetch(productUrl.pathname + productUrl.search, {
              method: 'GET',
              signal,
            });

            if (!response.ok) return null;

            const data = await response.text();

            const html = new DOMParser().parseFromString(data, 'text/html');

            const json = html.querySelector('[data-cart-cross-sell-card-json]');

            if (!json || !json.textContent) return null;

            const cardHtml = Object.values(JSON.parse(json.textContent))[0];
            const item = new DOMParser().parseFromString(String(cardHtml), 'text/html').body.firstElementChild;

            if (!(item instanceof HTMLElement)) return null;

            const available = item.getAttribute('data-available');

            if (available !== 'true') return null;

            item.dataset.clerkProductId = String(product.id);

            return item;
          });

          const items = await Promise.all(requests);

          return items.filter((item) => item !== null);
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            return [];
          }
          return [];
        }
      }
    },
  );
}
